<template>
  <card-container
    :id="pageId"
    :is-loading="isLoading"
    :disabled="isLoading || !isPhoneNumberValid || !requestBody.textContent.trim()"
    @submit="sendTextCustomer"
    @reset="resetForm"
  >
    <template #title>
      Text the Customer
    </template>

    <template #card-notes>
      <div class="card-notes">
        <p>This sends a text immediately from GoGo's text number. It will appear in the activity log.</p>
      </div>
    </template>

    <template #content>
      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Customer Name*</label>
        <div class="col-sm-7">
          <sanitized-input
            id="customerName"
            v-model="requestBody.customerName"
            placeholder="Robert Wenzel"
            required
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Customer Phone Number*</label>
        <phone-number-input
          id="customerPhoneNumber"
          v-model="requestBody.customerPhoneNumber"
          class="col-sm-7"
          :page-id="pageId"
        />
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Operator Name*</label>
        <div class="col-sm-7">
          <sanitized-input
            id="operatorName"
            v-model="requestBody.operatorName"
            placeholder="James Downey"
            required
          />
        </div>
      </div>

      <SmsConversationHistory
        :messages="messages"
        :is-loading="isLoadingHistory"
        :is-phone-number-valid="isPhoneNumberValid"
      />

      <div class="row">
        <label class="col-sm-3 control-label pt-5 mobile-pt-0">Text Content* </label>
        <div class="col-sm-7">
          <sanitized-textarea
            id="txtContent"
            v-model="requestBody.textContent"
            placeholder="The driver is waiting you to go to shopping."
            autosize
            required
          />
        </div>
      </div>
    </template>

    <template #submit-button>
      Send Text
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { computed, ref, Ref, watch } from 'vue';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useProfile } from '@/use/profile';
import { useSwalDialog } from '@/use/swalDialog';
import { useSmsConversationHistory } from '@/use/smsConversationHistory';
import { textMessageService } from '@/services/textMessage';
import { SmsRecipientName } from '@/gql/graphql';
import SmsConversationHistory from './SmsConversationHistory.vue';

const pageId = 'text-the-customer';

const { customerInfo, customerPhoneNumber } = useCustomer();
const { userFullName } = useProfile();
const { showSuccessPopupDialog, showErrorPopupDialog } = useSwalDialog();

const requestBody: Ref<any> = ref({
  customerName: '',
  customerPhoneNumber: '',
  operatorName: '',
  textContent: '',
});
const isLoading = ref(false);

const phoneNumber = computed(() => requestBody.value.customerPhoneNumber);
const { messages, isLoadingHistory, isPhoneNumberValid, loadMessageHistory } = useSmsConversationHistory(phoneNumber);

const sendTextCustomer = async () => {
  isLoading.value = true;
  try {
    await textMessageService.sendOperatorTextMessage({
      phoneNumber: requestBody.value.customerPhoneNumber,
      message: requestBody.value.textContent.trim(),
      recipientType: SmsRecipientName.Caller,
      associatedCallerId: customerInfo.value?.id,
      customerName: requestBody.value.customerName,
      operatorName: requestBody.value.operatorName,
    });

    requestBody.value.textContent = '';
    await loadMessageHistory();
    showSuccessPopupDialog({
      text: 'Text sent successfully.',
    });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
  isLoading.value = false;
};

const resetForm = () => {
  requestBody.value = {
    customerName: '',
    customerPhoneNumber: '',
    operatorName: userFullName.value,
    textContent: '',
  };
};

watch(
  customerInfo,
  (value: any) => {
    requestBody.value.customerName = util.getFullName(value.firstName, value.lastName);
    requestBody.value.operatorName = userFullName.value;
    requestBody.value.customerPhoneNumber = customerPhoneNumber.value;
  },
  {
    immediate: true,
  },
);
</script>
